// Import controller interfaces
import {
    IEndorsementController
} from '../Interfaces/ControllerInterfacesBarrel';
import ApiController from './ApiController';
// Import api models.
import {
    EndorsementPolicyTypeAndId, 
    EndorsementRequestCommercial,
    EndorsementRequestPersonal,
    EndorsementResponse,
    EndorsementResponseCostValues,
    ErrorMessage,
    PossiblePeril,
} from '@app/models/api';

export default class ProductionEndorsementController extends ApiController implements IEndorsementController {
    private readonly controllerUrl: string = 'v2/endorsement';

    public constructor() {
        super();
    }

    public async createEndorsement(policyId: number): Promise<number> {
        let url: string = `${this.controllerUrl}`;

        let data = {
            'policyId': policyId,
        };

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not create endorsement for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getEndorsementPolicyTypeAndId(endorsementId: number): Promise<EndorsementPolicyTypeAndId> {
        let url: string = `${this.controllerUrl}/policy-type-and-id/${endorsementId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get endorsement information.';
            }
            throw error;
        }
    }

    // #region personal-lines

    public async getEndorsementValuesPersonal(endorsementId: number): Promise<EndorsementResponse> {
        let url: string = `${this.controllerUrl}/personal/${endorsementId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get endorsement.';
            }
            throw error;
        }
    }

    public async updateEndorsementPersonal(endorsementId: number, endorsement?: EndorsementRequestPersonal, endorsementDate?: Date): Promise<boolean> {
        let url: string = `${this.controllerUrl}/personal/${endorsementId}`;

        let data = {
            'endorsementDate': endorsementDate ?? null,
            'policyInformation': endorsement ?? null,
        };

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not update endorsement for endorsement # ${endorsementId}.`;
            }
            throw error;
        }
    }

    // #endregion personal-lines

    // #region commercial-lines

    public async getEndorsementValuesCommercial(endorsementId: number): Promise<EndorsementResponse> {
        let url: string = `${this.controllerUrl}/commercial/${endorsementId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get endorsement.';
            }
            throw error;
        }
    }

    public async updateEndorsementCommercial(endorsementId: number, endorsement?: EndorsementRequestCommercial, endorsementDate?: Date): Promise<boolean> {
        let url: string = `${this.controllerUrl}/commercial/${endorsementId}`;

        let data = {
            'endorsementDate': endorsementDate ?? null,
            'policyInformation': endorsement ?? null,
        };

        try {
            let response: Response = await super.makePutCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not update endorsement for endorsement # ${endorsementId}.`;
            }
            throw error;
        }
    }

    // #endregion commercial-lines

    // #region calculation

    public async calculateEndorsement(endorsementId: number): Promise<EndorsementResponseCostValues> {
        let url: string = `${this.controllerUrl}/calculate/${endorsementId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not calculate for endorsement # ${endorsementId}.`;
            }
            throw error;
        }
    }

    // #endregion calculation

    // #region perils and deductibles

    public async getPossiblePerilsAndDeductibles(endorsementId: number, endorsementDate?: Date): Promise<PossiblePeril[]> {

        let url: string = `${this.controllerUrl}/possible-perils-deductibles/${endorsementId}`;
        if (endorsementDate !== undefined) {
            url += `/${endorsementDate.toISOString()}`;
        }

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to get possible perils and their deductibles for endorsement.';
            }
            throw error;
        }
    }

    // #endregion perils and deductibles
}